import type { MetaDataSchema } from '@mimir/common/mdf/metadataSchema';
import { normalizeFieldsBeforeSave, SchemaViewSanitizer } from '@mimir/common/mdf/metadataSchema';
import { doesSchemaSupportView } from '@mimir/common/mdf/MetaDataForm';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';
import Vue from 'vue';
import type { Module } from 'vuex';
import type { State } from './state';
import { logErrorAndShowToast } from '@mimir/web/util/uiError';
import { createMdf, getMdf, deleteMdf, putMdf } from '@mimir/web/util/api';
import { DEFAULT_MDF_ID } from '@mimir/common/mdf/mdfDefaults';
import { TIMED_METADATA_FLAVOR } from '@mimir/common/dto/timedMetadataModels';
import type { MdfCreationRequest } from '@mimir/common/dto/mdfModels';
import { ViewType } from '@mimir/common/dto/mdfModels';
import { showToast, TOAST } from '@mimir/web/eventBus';
import type { MdfModuleState } from './mdf-module-state';
import { TaskTracking } from '@mimir/common/async/taskTracking';


export const mdfModule: Module<MdfModuleState, State> = {
  state: {
    loadedSearchMdf: undefined,
    defaultMdf: null,
    mdfs: [],
    selectedMdf: null,
    mdfIsSaving: false,
    mdfsFieldsWithValidationErrors: {},
    sanitizeMetadataEditorLevel: 0,
  },
  mutations: {
    mdfs: (state, payload: MetaDataSchema[]) => {
      state.mdfs = payload;
      state.defaultMdf = findDefault(payload) ?? null;
    },
    addMdf: (state, payload: MetaDataSchema) => addMdf(state, payload),
    updateMdf: (state, payload: MetaDataSchema) => updateMdf(state, payload),
    removeMdf: (state, payload: string) => removeMdf(state, payload),
    setGlobalMdf: (state, payload: MetaDataSchema) => state.loadedSearchMdf = payload,
    unsetGlobalMdf: state => state.loadedSearchMdf = undefined,
    setSelectedMdf: (state, payload: MetaDataSchema) => state.selectedMdf = payload,
    setMdfIsSaving: (state, payload: boolean) => state.mdfIsSaving = payload,
    removeFieldIdValidatedState: (state, payload: string) => Vue.delete(state.mdfsFieldsWithValidationErrors, payload),
    setFieldIdValidatedState: (state, payload: { id: string, valid: boolean }) => Vue.set(state.mdfsFieldsWithValidationErrors, payload.id, payload.valid),
    clearMdfValidationState: state => Vue.set(state, 'mdfsFieldsWithValidationErrors', {}),
    shouldSanitizeMetadataEditor: state => state.sanitizeMetadataEditorLevel++,
  },
  getters: {
    sanitizeMetadataEditorLevel: state => state.sanitizeMetadataEditorLevel,
    mdfCanBeSaved: state => !Object.values(state.mdfsFieldsWithValidationErrors).includes(false),
    mdfs: state => {
      return [
        ...state.mdfs.filter(item => item.id === DEFAULT_MDF_ID),
        ...sortBy(state.mdfs.filter(item => item.id !== DEFAULT_MDF_ID), [item => item.label.toLowerCase()])
      ];
    },
    getMdfById: state => (id: string) => state.mdfs.find(mdf => mdf.id === id),
    getMdfByIdOrLabel: state => (idOrLabel: string) => state.mdfs.find(mdf => mdf.id === idOrLabel || mdf.label === idOrLabel),
    searchMdf: state => state.loadedSearchMdf,
    defaultMdf: state => state.defaultMdf,
    timedMdfs: state => sortBy(state.mdfs.filter(mdf => mdf.flavor === TIMED_METADATA_FLAVOR), [item => item.label.toLowerCase()]),
    hasTimedMdfs: state => state.mdfs.filter(mdf => mdf.flavor === TIMED_METADATA_FLAVOR).length > 0,
    selectedMdf: state => state.selectedMdf,
    mdfIsSaving: state => state.mdfIsSaving,
    userMadeChangesToSelectedMdf: state => {
      const { selectedMdf } = state;
      if (!selectedMdf) return false;
      const originalMdf = state.mdfs.find(mdf => mdf.id === selectedMdf.id);
      if (!originalMdf) return false;
      return !isEqual(selectedMdf, originalMdf);
    },
    selectedMdfDefaultFieldValueChanged: state => {
      const { selectedMdf } = state;
      if (!selectedMdf) return false;
      const originalMdf = state.mdfs.find(mdf => mdf.id === selectedMdf.id);
      if (!originalMdf) return false;
      for (const field of selectedMdf.fields) {
        const index = originalMdf.fields.findIndex(entry => entry.id === field.id);
        if (index >= 0 && !isEqual(originalMdf.fields[index].defaultValue, field.defaultValue)) {
          return true;
        }
      }
      const views = [ViewType.ITEM, ViewType.UPLOAD, ViewType.SHARE, ViewType.SCHEDULER, ViewType.COPY_ITEM, ViewType.NEW_VERSION, ViewType.CREATE_PLACEHOLDER];
      for (const view of views) {
        for (const selectedSetting of selectedMdf.views[view]) {
          const originalSetting = originalMdf.views[view]?.find(setting => selectedSetting.fieldId === setting.fieldId);
          if (!isEqual(originalSetting?.defaultValue, selectedSetting.defaultValue)) return true;
        }
      }
      return false;
    }
  },
  actions: {
    setMdfs: (context, payload: MetaDataSchema[]) => context.commit('mdfs', payload),
    addMdf: async (context, request: MdfCreationRequest) => {
      return createMdf(request).then(form => {
        context.commit('addMdf', form);
        context.commit('setSelectedMdf', cloneDeep(form));
      }).catch(err => logErrorAndShowToast('An error occurred while attempting to create MDF schema', err));
    },
    clearGlobalMdf: context => context.commit('unsetGlobalMdf'),
    setGlobalMdf: (context, payload: MetaDataSchema[]) => context.commit('setGlobalMdf', payload),
    getOrLoadMdf: (context, formIdOrLabel: string) => {
      // To invalidate cache, call loadMdf directly instead.
      const cachedMdf = context.state.mdfs.find(mdf => mdf.id === formIdOrLabel || mdf.label === formIdOrLabel);
      if (cachedMdf && cachedMdf.loaded) {
        return Promise.resolve(cachedMdf);
      } else {
        return context.dispatch('loadMdf', formIdOrLabel);
      }
    },
    loadMdf: (context, formIdOrLabel: string) =>
      getMdfPiggyBack.runOrPiggyback(formIdOrLabel, async () => {
        const form = await getMdf(formIdOrLabel);
        context.commit('updateMdf', form);
        return form;
      }),
    removeMdf: async (context, schemaId: string) =>
      deleteMdf(schemaId).then(response => {
        context.commit('removeMdf', schemaId);
        return response;
      }),
    resetSelectedMdf: async context => {
      const { selectedMdf } = context.state;
      if (!selectedMdf) return;
      const originalMdf = context.state.mdfs.find(mdf => mdf.id === selectedMdf.id);
      context.commit('setSelectedMdf', cloneDeep(originalMdf));
      context.commit('clearMdfValidationState');
    },
    loadAndSelectMdf: async (context, payload: { formIdOrLabel: string, sanitizeViews: boolean }) => {
      const loadedMdf: MetaDataSchema = await context.dispatch('loadMdf', payload.formIdOrLabel);
      if (payload.sanitizeViews) {
        const defaultMdf = context.state.defaultMdf;
        const viewSanitizer = SchemaViewSanitizer.getSchemaViewSanitizer(loadedMdf, defaultMdf);
        for (const view of Object.values(ViewType)) {
          if (!doesSchemaSupportView(loadedMdf.id, loadedMdf.flavor, view, loadedMdf.isSubtype)) continue;
          if (!loadedMdf.views[view]) Vue.set(loadedMdf.views, view, []);
          const sanitizedView = viewSanitizer.getSanitizedLayoutSettingsForView(view);
          Vue.set(loadedMdf.views, view, sanitizedView);
        }
      }
      context.commit('setSelectedMdf', cloneDeep(loadedMdf));
    },
    saveSelectedMdf: async context => {
      const { selectedMdf } = context.state;

      if (!selectedMdf) return;
      context.commit('setMdfIsSaving', true);

      const copyOfMDF = cloneDeep(selectedMdf);
      normalizeFieldsBeforeSave(copyOfMDF);
      return putMdf(copyOfMDF).then(savedForm => {
        context.commit('updateMdf', savedForm);
        context.commit('setSelectedMdf', cloneDeep(savedForm));
        showToast('Saved schema successfully', TOAST.SUCCESS);
      }).catch(err => {
        logErrorAndShowToast(err.response?.data?.error?.message || 'An error occured while saving schema, please contact your system administrator.', err);
      })
        .finally(() => context.commit('setMdfIsSaving', false));
    },
  },
};

const findDefault = (mdfs: MetaDataSchema[]): MetaDataSchema | undefined => {
  for (const schema of mdfs) {
    if (schema.id === DEFAULT_MDF_ID)
      return schema;
  }
  return undefined;
};

const addMdf = (state: MdfModuleState, mdf: MetaDataSchema) => {
  state.mdfs.push(mdf);
};

const removeMdf = (state: MdfModuleState, id: string) => {
  for (let i = 0; i < state.mdfs.length; i++) {
    if (state.mdfs[i].id === id) {
      state.mdfs.splice(i, 1);
      break;
    }
  }
};

const updateMdf = (state: MdfModuleState, mdf: MetaDataSchema) => {
  const existingIdx = state.mdfs.findIndex(s => s.id === mdf.id);
  if (existingIdx >= 0) {
    Vue.set(state.mdfs, existingIdx, mdf);
  } else {
    state.mdfs.push(mdf);
  }

  if (mdf.id === DEFAULT_MDF_ID) {
    state.defaultMdf = mdf;
  }
};

const getMdfPiggyBack = new TaskTracking<MetaDataSchema>();
